import { Extension, Node, mergeAttributes } from '@tiptap/core';

// A resume entry (job or school) needs its name/role on the left and its date
// range on the right, both on one visual line. Authoring that as two sibling
// paragraphs (the old pattern still found in existing resumes) has no shared
// row to lay out, so same-line alignment can only ever be approximated with
// float/margin tricks. This node makes the row a real parent: EntryHeaderName
// and EntryHeaderDate are its only two children, laid out with flexbox, so
// alignment holds regardless of how long either side's text is.
//
// Both children keep their own `blockId` (via BlockIdExtension — see that
// file's BLOCK_TYPES) so comments and diff hunks can still address the name
// and the date independently, exactly as they could with the old two-<p>
// pattern.

const EntryHeaderName = Node.create({
    name: 'entryHeaderName',
    content: 'inline*',
    isolating: true,

    parseHTML() {
        return [{ tag: 'div[data-type="entry-header-name"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'entry-header-name',
            }),
            0,
        ];
    },
});

const EntryHeaderDate = Node.create({
    name: 'entryHeaderDate',
    content: 'inline*',
    isolating: true,

    parseHTML() {
        return [{ tag: 'div[data-type="entry-header-date"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'entry-header-date',
            }),
            0,
        ];
    },
});

// ProseMirror repairs missing children by filling the first empty slot, not
// by matching node type — so a hand-authored row that carries only a date div
// would be parsed as a *name*. Normalizing the children into a fixed
// [name, date] pair before ProseMirror sees them keeps each side's meaning.
const normalizeEntryHeaderContent = (element) => {
    const doc = element.ownerDocument;
    const wrapper = doc.createElement('div');
    ['name', 'date'].forEach((side) => {
        const type = `entry-header-${side}`;
        const existing = element.querySelector(`:scope > div[data-type="${type}"]`);
        const child = existing
            ? existing.cloneNode(true)
            : doc.createElement('div');
        child.setAttribute('data-type', type);
        wrapper.appendChild(child);
    });
    return wrapper;
};

const EntryHeader = Node.create({
    name: 'entryHeader',
    group: 'block',
    // Fixed shape on purpose: exactly one name side and one date side, in
    // that order, so the schema itself rules out a row with the wrong
    // number of sides or the date ending up first.
    content: 'entryHeaderName entryHeaderDate',
    isolating: true,

    parseHTML() {
        return [
            {
                tag: 'div[data-type="entry-header"]',
                contentElement: normalizeEntryHeaderContent,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-type': 'entry-header' }),
            0,
        ];
    },

    // The row is `isolating`, so without an explicit escape hatch Enter is
    // swallowed and a header inserted as the last block can never be
    // followed by a paragraph.
    addKeyboardShortcuts() {
        const exitRow = () => {
            const { $from } = this.editor.state.selection;
            for (let depth = $from.depth; depth > 0; depth -= 1) {
                if ($from.node(depth).type.name !== this.name) continue;
                const after = $from.after(depth);
                return this.editor
                    .chain()
                    .insertContentAt(after, { type: 'paragraph' })
                    .setTextSelection(after + 1)
                    .focus()
                    .run();
            }
            return false;
        };
        return { Enter: exitRow, 'Mod-Enter': exitRow };
    },

    addCommands() {
        return {
            insertEntryHeader:
                () =>
                    ({ chain, editor }) =>
                        // A list item accepts `block*`, so an unguarded insert
                        // would nest the row under a bullet.
                        !editor.isActive('listItem') &&
                        chain()
                            .insertContent({
                                type: this.name,
                                content: [
                                    {
                                        type: 'entryHeaderName',
                                        content: [
                                            {
                                                type: 'text',
                                                text: 'Company, City, State | Role',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'entryHeaderDate',
                                        content: [
                                            {
                                                type: 'text',
                                                text: 'Start – End',
                                            },
                                        ],
                                    },
                                ],
                            })
                            .run(),
        };
    },
});

// Legacy resumes author an entry row as two adjacent paragraphs, the second
// right-aligned. Right alignment alone is too broad a signal (a right-aligned
// contact line would be floated onto the line above it), so mark a paragraph
// as a date only when it also follows a paragraph and reads like a date range.
const ENTRY_DATE_PATTERN = /(19|20)\d{2}|present/i;

const isEntryDateParagraph = (element) => {
    const style = element.getAttribute('style') || '';
    if (!/text-align:\s*right/i.test(style)) return false;
    const previous = element.previousElementSibling;
    if (!previous || previous.tagName !== 'P') return false;
    return ENTRY_DATE_PATTERN.test(element.textContent || '');
};

const EntryDateParagraph = Extension.create({
    name: 'entryDateParagraph',

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph'],
                attributes: {
                    entryDate: {
                        default: null,
                        parseHTML: (element) =>
                            isEntryDateParagraph(element) ? 'true' : null,
                        renderHTML: (attributes) =>
                            attributes.entryDate
                                ? { 'data-entry-date': 'true' }
                                : {},
                    },
                },
            },
        ];
    },
});

export {
    EntryHeader,
    EntryHeaderName,
    EntryHeaderDate,
    EntryDateParagraph,
};
